import { AddDiscountCodeStatus, StateCart } from '@spa/lib/store/carts/CartsState';
import {
  type AddEmployeeDiscountCodeToCurrentCartsRequestedAction,
  addEmployeeDiscountCodeToCurrentCartsFailed,
  addEmployeeDiscountCodeToCurrentCartsSucceeded,
  setCart
} from '../../carts/cartsSlice';
import {
  ApiAddEmployeeDiscountCodesFailedAction,
  ApiAddEmployeeDiscountCodesSucceededAction,
  apiAddEmployeeDiscountCodesFailedType,
  apiAddEmployeeDiscountCodesRequested,
  apiAddEmployeeDiscountCodesSucceededType
} from '@spa/lib/store/api/cart/addEmployeeDiscountCodeApi';
import { put, race, select, take } from 'typed-redux-saga';
import { CustomOrderType } from '@spa/lib/store/carts/CustomOrderType';
import convertCartToStateCart from '@spa/lib/converters/convertCartToStateCart';
import generateActionId from '@spa/lib/generateActionId';
import selectCarts from '@spa/lib/store/carts/selectors/selectCarts';
import selectEmployeeDiscountBlockingEntries from '@spa/lib/store/carts/selectors/selectEmployeeDiscountBlockingEntries';

type ApplyResult =
  | { type: 'succeeded'; customOrderType: CustomOrderType; cart: StateCart | null }
  | { type: 'skipped' }
  | { type: 'failed' };

function* applyEmployeeDiscountToCartWithEntries(
  customOrderType: CustomOrderType,
  requestedActionId: number
): Generator<unknown, ApplyResult> {
  const cart: StateCart | null = (yield* select(selectCarts))[customOrderType];

  if (cart === null || cart.entries.length === 0) {
    return { type: 'skipped' };
  }

  const actionId: number = generateActionId();

  yield* put(
    apiAddEmployeeDiscountCodesRequested({
      actionId,
      customOrderType,
      parentActionId: requestedActionId
    })
  );

  let isSameActionId: boolean = false;
  let result: ApplyResult | undefined;

  do {
    const {
      succeeded,
      failed
    }: {
      succeeded: ApiAddEmployeeDiscountCodesSucceededAction | undefined;
      failed: ApiAddEmployeeDiscountCodesFailedAction | undefined;
    } = yield* race({
      succeeded: take<ApiAddEmployeeDiscountCodesSucceededAction>(
        apiAddEmployeeDiscountCodesSucceededType
      ),
      failed: take<ApiAddEmployeeDiscountCodesFailedAction>(apiAddEmployeeDiscountCodesFailedType)
    });

    isSameActionId =
      succeeded?.payload.requestedActionId === actionId ||
      failed?.payload.requestedActionId === actionId;

    if (isSameActionId) {
      if (succeeded !== undefined) {
        const rawCart = succeeded.payload.result?.cart ?? null;
        result = {
          type: 'succeeded',
          customOrderType,
          cart: rawCart ? convertCartToStateCart(rawCart) : null
        };
      } else {
        result = { type: 'failed' };
      }
    }
  } while (!isSameActionId);

  return result!; // eslint-disable-line @typescript-eslint/no-non-null-assertion
}

export function* addEmployeeDiscountCodesToCurrentCartsSaga({
  payload: { requestedActionId }
}: AddEmployeeDiscountCodeToCurrentCartsRequestedAction): Generator {
  const blockingEntries = yield* select(selectEmployeeDiscountBlockingEntries);
  if (blockingEntries.length > 0) {
    yield* put(
      addEmployeeDiscountCodeToCurrentCartsFailed({
        requestedActionId,
        redemptionStatus: AddDiscountCodeStatus.NOT_APPLIED
      })
    );
    return;
  }

  const homeDeliveryResult: ApplyResult = yield* applyEmployeeDiscountToCartWithEntries(
    CustomOrderType.HOME_DELIVERY,
    requestedActionId
  );

  const collectAtStoreResult: ApplyResult = yield* applyEmployeeDiscountToCartWithEntries(
    CustomOrderType.COLLECT_AT_STORE,
    requestedActionId
  );

  // A "succeeded" result only means the mutation call itself did not error.
  // applyEmployeeDiscount still returns a successful result carrying the
  // cart unchanged when it detects a ProductDiscount blocker (defense in
  // depth against the blockingEntries check above becoming stale during
  // this saga's sequential HD/C&C round trips). Treat that case as not
  // applied so we never report APPLIED for a cart that is still blocked.
  const wasActuallyApplied = (result: ApplyResult): boolean =>
    result.type === 'succeeded' &&
    !(result.cart?.entries ?? []).some(({ hasProductDiscount }) => hasProductDiscount === true);

  const anySucceeded =
    wasActuallyApplied(homeDeliveryResult) || wasActuallyApplied(collectAtStoreResult);

  for (const applyResult of [homeDeliveryResult, collectAtStoreResult]) {
    if (applyResult.type === 'succeeded' && applyResult.cart !== null) {
      yield* put(
        setCart({
          localChange: true,
          cart: applyResult.cart,
          customOrderType: applyResult.customOrderType
        })
      );
    }
  }

  if (anySucceeded) {
    yield* put(
      addEmployeeDiscountCodeToCurrentCartsSucceeded({
        requestedActionId,
        redemptionStatus: AddDiscountCodeStatus.APPLIED
      })
    );
  } else {
    yield* put(
      addEmployeeDiscountCodeToCurrentCartsFailed({
        requestedActionId,
        redemptionStatus: AddDiscountCodeStatus.REDEMPTION_ERROR
      })
    );
  }
}
